import produce from 'immer';
import { asDateString, WidgetTab } from '../../common';
import { changed, isIn, isPresent } from '../../utils';
import { BookingState } from '../state';
import {
  allSelected,
  calendarAvailable,
  calendarChanged,
  locationsSet,
  preDatesSelected,
  preDatesSelectedAndNotOneWay,
  selectionChanged,
} from '../stateUtils';
import { LifecycleEvent, SideEffect, SideEffectGenerator, Store } from '../types';
import { bindActions } from 'redux-zero/utils';

export const REGULATORY_NOTIFICATION_CITIES: string[] = ['HND', 'BJS', 'PEK', 'NKG', 'PKX', 'CKG', 'HKG'];

export const setProceedEnabledEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _setProceedEnabled: (state) =>
        produce(state, ({ selection, ux }: BookingState) => {
          ux.proceedEnabled = allSelected(selection);
        }),
    },
    store
  );

  return {
    runSideEffects: effectActions._setProceedEnabled,
    when: (prev, next) => prev.ux.proceedEnabled !== allSelected(next.selection),
    name: setProceedEnabledEffect.name,
  };
};

export const setDepartureDateEnabledEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _setDepartureDateEnabled: (state) =>
        produce(state, ({ selection, ux, currentTab }: BookingState) => {
          ux.departureDateEnabled =
            currentTab === WidgetTab.AM || (preDatesSelected(selection) && calendarAvailable(ux));
        }),
    },
    store
  );

  return {
    runSideEffects: effectActions._setDepartureDateEnabled,
    when: (prev, next) =>
      (selectionChanged(prev, next) || calendarChanged(prev, next)) &&
      next.ux.departureDateEnabled !== (preDatesSelected(next.selection) && calendarAvailable(next.ux)),
    name: setDepartureDateEnabledEffect.name,
  };
};

export const setReturnDateEnabledEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _setReturnDateEnabled: (state) =>
        produce(state, ({ selection, ux, currentTab }: BookingState) => {
          ux.returnDateEnabled =
            currentTab === WidgetTab.AM || (preDatesSelectedAndNotOneWay(selection) && calendarAvailable(ux));
        }),
    },
    store
  );

  return {
    runSideEffects: effectActions._setReturnDateEnabled,
    when: (prev, next) =>
      (selectionChanged(prev, next) || calendarChanged(prev, next)) &&
      next.ux.returnDateEnabled !== (preDatesSelectedAndNotOneWay(next.selection) && calendarAvailable(next.ux)),
    name: setReturnDateEnabledEffect.name,
  };
};

export const setRegulatoryWarningEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _setRegulatoryWarning: (state) =>
        produce(state, ({ selection, ux }: BookingState) => {
          const { origin, destination } = selection.locations[selection.activeLocationIndex];
          let regulatoryApprovalAirport: string;

          if (isIn(origin.locationCode, REGULATORY_NOTIFICATION_CITIES)) {
            regulatoryApprovalAirport = origin.title;
          } else if (isIn(destination.locationCode, REGULATORY_NOTIFICATION_CITIES)) {
            regulatoryApprovalAirport = destination.title;
          }

          ux.warnings.regulatory = isPresent(regulatoryApprovalAirport);
          ux.regulatoryWarningAirport = regulatoryApprovalAirport;
        }),
    },
    store
  );

  return {
    runSideEffects: effectActions._setRegulatoryWarning,
    when: (prev, next) => changed(prev, next, 'selection', 'locations') && locationsSet(next.selection),
    name: setRegulatoryWarningEffect.name,
  };
};

export const updateCurrentDateEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _updateCurrentDate: (state) =>
        produce(state, ({ ux }: BookingState) => {
          if (ux.clientMsOffset != null) {
            ux.currentDate = asDateString(new Date(Date.now() - ux.clientMsOffset));
          }
        }),
    },
    store
  );

  return {
    runSideEffects: effectActions._updateCurrentDate,
    when: () => false,
    runOn: [{ type: LifecycleEvent.INTERVAL }],
    name: updateCurrentDateEffect.name,
  };
};

export const uxStateEffects: SideEffectGenerator<BookingState>[] = [
  setProceedEnabledEffect,
  setDepartureDateEnabledEffect,
  setReturnDateEnabledEffect,
  setRegulatoryWarningEffect,
  updateCurrentDateEffect,
];
